import React, { useMemo } from "react";
import { cn } from "../../utils/cn";

/**
 * SafeMarkdown — a minimal, dependency-free markdown renderer for legal copy.
 *
 * We intentionally avoid `dangerouslySetInnerHTML` on raw input. The parser
 * walks markdown line-by-line and emits real React nodes for a small, audited
 * subset of CommonMark:
 *
 *   - ATX headings: #, ##, ###
 *   - paragraphs (blank-line separated)
 *   - unordered lists  ("- " or "* ")
 *   - ordered lists    ("1. ")
 *   - horizontal rule  ("---")
 *   - blockquote       ("> ")
 *   - inline:  **bold**, *italic*, `code`, [link text](https://…)
 *
 * Anything that doesn't match falls through as plain text. Link `href`s are
 * validated against an allowlist (http / https / mailto) — anything else is
 * rendered as text, not a link, defeating `javascript:` / `data:` payloads.
 */

interface SafeMarkdownProps {
  content: string;
  className?: string;
}

const SAFE_PROTOCOL_RE = /^(https?:\/\/|mailto:)/i;

function escapeForReact(text: string): string {
  return text;
}

/* ────────────────── Inline parser ────────────────── */
/** Render a single line with inline marks. */
function renderInline(text: string, keyPrefix: string): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  let cursor = 0;
  let idx = 0;

  // Composite regex: code, bold, italic, link
  // Order matters — code first so its contents aren't reparsed.
  const re = /(`([^`]+)`)|(\*\*([^*]+)\*\*)|(\*([^*\n]+)\*)|(\[([^\]]+)\]\(([^)]+)\))/g;

  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (m.index > cursor) {
      out.push(escapeForReact(text.slice(cursor, m.index)));
    }
    if (m[1]) {
      out.push(
        <code
          key={`${keyPrefix}-c-${idx++}`}
          className="px-1.5 py-0.5 rounded bg-muted text-foreground text-[0.85em] font-mono"
        >
          {m[2]}
        </code>,
      );
    } else if (m[3]) {
      out.push(
        <strong key={`${keyPrefix}-b-${idx++}`} className="font-semibold text-foreground">
          {m[4]}
        </strong>,
      );
    } else if (m[5]) {
      out.push(
        <em key={`${keyPrefix}-i-${idx++}`} className="italic">
          {m[6]}
        </em>,
      );
    } else if (m[7]) {
      const label = m[8];
      const href = m[9];
      if (SAFE_PROTOCOL_RE.test(href)) {
        out.push(
          <a
            key={`${keyPrefix}-a-${idx++}`}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary underline underline-offset-2 hover:opacity-80"
          >
            {label}
          </a>,
        );
      } else {
        // Strip unsafe protocols — fall back to plain text
        out.push(`${label} (${href})`);
      }
    }
    cursor = re.lastIndex;
  }
  if (cursor < text.length) out.push(escapeForReact(text.slice(cursor)));
  return out;
}

/* ────────────────── Block parser ────────────────── */
interface Block {
  type:
    | "h1"
    | "h2"
    | "h3"
    | "p"
    | "ul"
    | "ol"
    | "hr"
    | "blockquote";
  lines: string[];
}

function parseBlocks(src: string): Block[] {
  const lines = src.replace(/\r\n/g, "\n").split("\n");
  const blocks: Block[] = [];
  let i = 0;
  const pushPara = (buf: string[]) => {
    if (buf.length === 0) return;
    blocks.push({ type: "p", lines: buf.slice() });
  };

  let para: string[] = [];

  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    // blank line — flush paragraph
    if (trimmed === "") {
      pushPara(para);
      para = [];
      i++;
      continue;
    }

    // horizontal rule
    if (/^---+$/.test(trimmed) || /^\*\*\*+$/.test(trimmed)) {
      pushPara(para);
      para = [];
      blocks.push({ type: "hr", lines: [] });
      i++;
      continue;
    }

    // headings
    const h = /^(#{1,3})\s+(.*)$/.exec(trimmed);
    if (h) {
      pushPara(para);
      para = [];
      const level = h[1].length;
      const type = level === 1 ? "h1" : level === 2 ? "h2" : "h3";
      blocks.push({ type, lines: [h[2]] });
      i++;
      continue;
    }

    // blockquote
    if (/^>\s?/.test(trimmed)) {
      pushPara(para);
      para = [];
      const qLines: string[] = [];
      while (i < lines.length && /^>\s?/.test(lines[i].trim())) {
        qLines.push(lines[i].trim().replace(/^>\s?/, ""));
        i++;
      }
      blocks.push({ type: "blockquote", lines: qLines });
      continue;
    }

    // unordered list
    if (/^[-*]\s+/.test(trimmed)) {
      pushPara(para);
      para = [];
      const items: string[] = [];
      while (i < lines.length && /^[-*]\s+/.test(lines[i].trim())) {
        items.push(lines[i].trim().replace(/^[-*]\s+/, ""));
        i++;
      }
      blocks.push({ type: "ul", lines: items });
      continue;
    }

    // ordered list
    if (/^\d+\.\s+/.test(trimmed)) {
      pushPara(para);
      para = [];
      const items: string[] = [];
      while (i < lines.length && /^\d+\.\s+/.test(lines[i].trim())) {
        items.push(lines[i].trim().replace(/^\d+\.\s+/, ""));
        i++;
      }
      blocks.push({ type: "ol", lines: items });
      continue;
    }

    // paragraph continuation
    para.push(trimmed);
    i++;
  }
  pushPara(para);

  return blocks;
}

const SafeMarkdown: React.FC<SafeMarkdownProps> = ({ content, className }) => {
  const blocks = useMemo(() => parseBlocks(content || ""), [content]);

  return (
    <div className={cn("space-y-3 text-sm leading-relaxed text-foreground", className)}>
      {blocks.map((b, idx) => {
        const k = `b-${idx}`;
        switch (b.type) {
          case "h1":
            return (
              <h1 key={k} className="text-2xl font-bold tracking-tight text-foreground mt-2">
                {renderInline(b.lines[0], k)}
              </h1>
            );
          case "h2":
            return (
              <h2 key={k} className="text-lg font-semibold text-foreground mt-4">
                {renderInline(b.lines[0], k)}
              </h2>
            );
          case "h3":
            return (
              <h3 key={k} className="text-base font-semibold text-foreground mt-3">
                {renderInline(b.lines[0], k)}
              </h3>
            );
          case "hr":
            return <hr key={k} className="my-4 border-border" />;
          case "blockquote":
            return (
              <blockquote
                key={k}
                className="border-s-2 border-primary/40 ps-3 py-1 text-muted-foreground italic"
              >
                {b.lines.map((line, li) => (
                  <p key={`${k}-q-${li}`}>{renderInline(line, `${k}-q-${li}`)}</p>
                ))}
              </blockquote>
            );
          case "ul":
            return (
              <ul key={k} className="list-disc ps-6 space-y-1">
                {b.lines.map((line, li) => (
                  <li key={`${k}-li-${li}`}>{renderInline(line, `${k}-li-${li}`)}</li>
                ))}
              </ul>
            );
          case "ol":
            return (
              <ol key={k} className="list-decimal ps-6 space-y-1">
                {b.lines.map((line, li) => (
                  <li key={`${k}-li-${li}`}>{renderInline(line, `${k}-li-${li}`)}</li>
                ))}
              </ol>
            );
          case "p":
          default:
            return (
              <p key={k} className="text-foreground/90">
                {b.lines.map((line, li) => (
                  <React.Fragment key={`${k}-l-${li}`}>
                    {li > 0 ? " " : null}
                    {renderInline(line, `${k}-l-${li}`)}
                  </React.Fragment>
                ))}
              </p>
            );
        }
      })}
    </div>
  );
};

export default SafeMarkdown;
